import { useMemo, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useSearchParams } from 'react-router-dom';
import { PageHeader } from '@/components/layout/PageHeader';
import { ICONS } from '@/constants/icons';
import { useAnalyticsStore } from '@/stores/analyticsStore';
import { useAnalyticsHub } from '@/hooks/useAnalytics';
import { formatApiError } from '@/services/api';
import { AnalyticsHubFilters, AnalyticsHubSummary } from '@/types/analytics';
import { AnalyticsFilters } from '@/components/analytics/AnalyticsFilters';
import { AnalyticsHeroStats } from '@/components/analytics/AnalyticsHeroStats';
import { AnalyticsTable } from '@/components/analytics/AnalyticsTable';
import { AnalyticsDetailDrawer } from '@/components/analytics/AnalyticsDetailDrawer';
import { AnalyticsCompare } from '@/components/analytics/AnalyticsCompare';
import { AIInsightsWidget } from '@/components/dashboard/AIInsightsWidget';
import { FeatureGate } from '@/components/gating/FeatureGate';
import { LoadingSpinner } from '@/components/LoadingSpinner';
import { useLockedEntry, usePackagePending } from '@/hooks/useLockedEntry';
import { LockedFeatureCard } from '@/components/gating/LockedFeatureCard';
import { useEntitlement } from '@/hooks/useEntitlement';

/**
 * Analytics hub page. When the plan does not include `analytics_hub` (enforce) the whole page is the locked
 * teaser and no analytics request is made.
 */
export const Analytics = () => {
  const { t } = useTranslation(['analytics', 'common']);
  const hub = useEntitlement('analytics_hub');
  const base = useLockedEntry('analytics');
  // Locked when the hub is locked (enforce) or the base analytics switch of the plan is off (any mode)
  const locked = hub.locked || base.locked;
  const pending = usePackagePending();
  const upsellPlanSlug = hub.locked ? hub.upsellPlanSlug : base.upsellPlanSlug ?? hub.upsellPlanSlug;

  // Package not known yet: loading state, no content mount and no data fetch before the decision
  if (pending) {
    return (
      <div className="p-6">
        <LoadingSpinner />
      </div>
    );
  }

  if (locked) {
    return (
      <div>
        <PageHeader
          title={t('analytics:analytics_hub')}
          subtitle={t('analytics:analytics_hub_subtitle')}
          icon={ICONS.GRAPH}
          headerType="list"
          documentTitle={t('analytics:analytics_hub')}
        />
        <div className="mt-6 sm:mt-0 max-w-2xl mx-auto">
          <LockedFeatureCard feature="analytics_hub" upsellPlanSlug={upsellPlanSlug} />
        </div>
      </div>
    );
  }

  return <AnalyticsContent />;
};

const AnalyticsContent = () => {
  const { t } = useTranslation(['analytics', 'common']);
  const store = useAnalyticsStore();
  const compareMode = useAnalyticsStore((s) => s.compareMode);
  const [searchParams, setSearchParams] = useSearchParams();

  // Auto-open drawer if ?post=<channelInstanceId> is in URL
  useEffect(() => {
    const postParam = searchParams.get('post');
    if (postParam) {
      const channelInstanceId = parseInt(postParam, 10);
      if (!isNaN(channelInstanceId) && channelInstanceId > 0) {
        store.setSelectedChannelInstanceId(channelInstanceId);
        // Clean the URL param after consuming it
        searchParams.delete('post');
        setSearchParams(searchParams, { replace: true });
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []); // Only on mount

  // Memoize date range to prevent getDateRange() from creating new Date().toISOString()
  // on every render, which would cause infinite query refetching
  const dateRange = useMemo(
    () => store.getDateRange(),
    [store.timeRange, store.customFrom, store.customTo]
  );

  const filters: AnalyticsHubFilters = useMemo(() => ({
    page: store.page,
    limit: store.limit,
    sortBy: store.sortBy,
    sortOrder: store.sortOrder,
    from: dateRange.from,
    to: dateRange.to,
    platform: store.platform || undefined,
    channelId: store.channelId || undefined,
  }), [store.page, store.limit, store.sortBy, store.sortOrder, dateRange.from, dateRange.to, store.platform, store.channelId]);

  const { data, isLoading, isFetching, error } = useAnalyticsHub(filters);

  const emptySummary: AnalyticsHubSummary = {
    totalReach: 0,
    totalImpressions: 0,
    totalEngagement: 0,
    avgEngagementRate: 0,
    postsCount: 0,
    bestPerformer: null,
  };

  // Show error state if query failed
  if (error) {
    return (
      <div>
        <PageHeader
          title={t('analytics:analytics_hub')}
          subtitle={t('analytics:analytics_hub_subtitle')}
          icon={ICONS.GRAPH}
          headerType="list"
          documentTitle={t('analytics:analytics_hub')}
        />
        <div className="bg-white rounded-lg border border-red-200 p-8 text-center">
          <i className="hm hm-warning text-red-500 text-4xl mb-4 block" />
          <h3 className="text-lg font-semibold text-gray-900 mb-2">
            {t('analytics:error_loading', { defaultValue: 'Failed to load analytics' })}
          </h3>
          <p className="text-gray-600 mb-4">
            {formatApiError(error).message}
          </p>
          <button
            onClick={() => window.location.reload()}
            className="px-4 py-2 bg-primary-600 text-primary-600-contrast rounded-lg hover:bg-primary-700"
          >
            {t('common:try_again', { defaultValue: 'Try Again' })}
          </button>
        </div>
      </div>
    );
  }

  return (
    <div>
      <PageHeader
        title={t('analytics:analytics_hub')}
        subtitle={t('analytics:analytics_hub_subtitle')}
        icon={ICONS.GRAPH}
        headerType="list"
        documentTitle={t('analytics:analytics_hub')}
      />

      <div className="space-y-6 mt-6 sm:mt-0">
        <AnalyticsFilters />

        <AnalyticsHeroStats
          summary={data?.summary ?? emptySummary}
          previousPeriodSummary={data?.previousPeriodSummary ?? emptySummary}
          isLoading={isLoading}
          onBestPerformerClick={(channelInstanceId) => store.setSelectedChannelInstanceId(channelInstanceId)}
        />

        {/* AI Insights — interpretation layer above the raw analytics table.
            Empty state (with Refresh CTA) is intentional here: this is the
            canonical home where users come to trigger generation. */}
        <AIInsightsWidget />

        {compareMode !== 'none' ? (
          <FeatureGate feature="analytics_compare">
            <AnalyticsCompare
              compareMode={compareMode}
              summary={data?.summary ?? emptySummary}
              previousPeriodSummary={data?.previousPeriodSummary ?? emptySummary}
              platformBreakdown={data?.platformBreakdown ?? []}
              channelBreakdown={data?.channelBreakdown ?? []}
            />
          </FeatureGate>
        ) : (
          <AnalyticsTable
            data={data?.data ?? []}
            pagination={data?.pagination ?? { page: 1, limit: 20, total: 0, pages: 0 }}
            isLoading={isLoading}
            isFetching={isFetching}
          />
        )}

        <AnalyticsDetailDrawer />
      </div>
    </div>
  );
};
